/* eslint-disable react-hooks/exhaustive-deps */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { QuoteContext } from './quoteContext';
import { ConfirmationData } from '../components/common/summary-common/confirmationData';
import { defaultCoverageInformation, updateCoverageInformation } from '../components/application/BR/templates/confirmation/defaultCoverageInformation';
import { overWritePersonalInformationOfTheInsured } from '../components/application/BR/templates/confirmation/defaultPersonalInformationOfTheInsured';
import { overWriteOccupationInformation } from '../components/application/BR/templates/confirmation/defaultOccupationInformation';
import { overWriteIncomeInformation } from '../components/application/BR/templates/confirmation/defaultIncomeInformation';
import { getApplicationSummary } from '../api/applicationService';
import { ApplicationSummary, findValue, updateSummaryPageInfo } from '../components/common/summary-common/applicationSummary';
import { useLocation, useNavigate } from 'react-router-dom';
import { overWritePoliticallyExposedPersonInformation } from '../components/application/BR/templates/confirmation/defaultPoliticallyExposedPersonInformation';
import { overWriteRiskyActivitiesInformation } from '../components/application/BR/templates/confirmation/defaultRiskyActivitiesInformation';
import { overWriteFamilyMedicalHistoryInformation } from '../components/application/BR/templates/confirmation/defaultFamilyMedicalHistoryInformation';
import { overWriteInsuranceInformation } from '../components/application/BR/templates/confirmation/defaultInsuranceInformation';
import { overWriteBeneficiaryInformation } from '../components/application/BR/templates/confirmation/defaultBeneficiaryInformation';
import { overWriteMedicalInformation } from '../components/application/BR/templates/confirmation/defaultMedicalInformation';
import { overWritePhysicalInformation } from '../components/application/BR/templates/confirmation/defaultPhysicalInformation';
import { overWritePolicyOwnerInformation } from '../components/application/BR/templates/confirmation/defaultPolicyOwnerInformation';

const combinePagesInOrder = (
    pageIds: string[],
    applicationSummary: ApplicationSummary[],
    overWriteFunction: (json: any, language: "es" | "en" | "pt", isPEP?: boolean, isRisky?: boolean) => Map<string, ConfirmationData>,
    language: "es" | "en" | "pt",
    isPEP: boolean = false,
    isRisky: boolean = false
): Map<string, ConfirmationData> => {
    const orderedPages = new Map();
    pageIds.forEach(id => {
        const page = applicationSummary.find(page => page.template_page_id === id);
        if (page) {
            orderedPages.set(id, page);
        }
    });

    if (orderedPages.size > 0) {
        const combinedJson = {
            ...orderedPages.values().next().value.json,
            sections: Array.from(orderedPages.values()).flatMap(page => page.json?.sections || [])
        };
        return overWriteFunction(combinedJson, language, isPEP, isRisky);
    }

    return new Map();
};

interface ConfirmationContextType {
    isLoading: boolean;
    setIsLoading: (value: boolean) => void;
    coverageInformation: Map<string, ConfirmationData>;
    setCoverageInformation: (values: Map<string, ConfirmationData>) => void;
    personalInformationOfTheInsured: Map<string, ConfirmationData>;
    setPersonalInformationOfTheInsured: (values: Map<string, ConfirmationData>) => void;
    occupationInformation: Map<string, ConfirmationData>;
    setOccupationInformation: (values: Map<string, ConfirmationData>) => void;
    incomeInformation: Map<string, ConfirmationData>;
    setIncomeInformation: (values: Map<string, ConfirmationData>) => void;
    politicallyExposedPersonInformation: Map<string, ConfirmationData>;
    setPoliticallyExposedPersonInformation: (values: Map<string, ConfirmationData>) => void;
    riskyActivitiesInformation: Map<string, ConfirmationData>;
    setRiskyActivitiesInformation: (values: Map<string, ConfirmationData>) => void;
    familyMedicalHistoryInformation: Map<string, ConfirmationData>;
    setFamilyMedicalHistoryInformation: (values: Map<string, ConfirmationData>) => void;
    insuranceInformation: Map<string, ConfirmationData>;
    setInsuranceInformation: (values: Map<string, ConfirmationData>) => void;
    beneficiaryInformation: Map<string, ConfirmationData>;
    setBeneficiaryInformation: (values: Map<string, ConfirmationData>) => void;
    medicalInformation: Map<string, ConfirmationData>;
    setMedicalInformation: (values: Map<string, ConfirmationData>) => void;
    physicalInformation: Map<string, ConfirmationData>;
    setPhysicalInformation: (values: Map<string, ConfirmationData>) => void;
    policyOwnerInformation: Map<string, ConfirmationData>;
    setPolicyOwnerInformation: (values: Map<string, ConfirmationData>) => void;
    email: string;
    setEmail: (value: string) => void;
    firstName: string;
    setFirstName: (value: string) => void;
    lastName: string;
    setLastName: (value: string) => void;
    phone: string;
    setPhone: (value: string) => void;
    address: string;
    setAddress: (value: string) => void;
    city: string;
    setCity: (value: string) => void;
    state: string;
    setState: (value: string) => void;
    country: string;
    setCountry: (value: string) => void;
    zipCode: string;
    setZipCode: (value: string) => void;
    backToApplication: () => void;
    getPageIdByTemplateId: (templateId: string) => number;
    findValueByGroupAndQuestion: (groupAndQuestionId: string) => string | undefined;
    getFieldsBySectionId: (sectionId: string, excludeGroupId?: string) => any[];
    findGroupWithBothConditions: (sectionId: string, questionId1: string, value1: string, questionId2: string, value2: string) => boolean;
    getHealthInsuranceBySectionId: (sectionId: string, excludeGroupId?: string) => Array<{ company: string; coverage_amount: number | null }>;
    getOccupationBySectionId: (sectionId: string, excludeGroupId?: string) => Array<{ name: string; description: string; main?: boolean; other_name?: string }>;
    refreshData: () => Promise<void>;
}

const BrConfirmationContext = createContext<ConfirmationContextType>({} as ConfirmationContextType);

export const BrConfirmationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { selected, lastQuote, quoteID } = useContext(QuoteContext);
    const [coverageInformation, setCoverageInformation] = useState<Map<string, ConfirmationData>>(defaultCoverageInformation);
    const [personalInformationOfTheInsured, setPersonalInformationOfTheInsured] = useState<Map<string, ConfirmationData>>(new Map());
    const [occupationInformation, setOccupationInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [incomeInformation, setIncomeInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [politicallyExposedPersonInformation, setPoliticallyExposedPersonInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [riskyActivitiesInformation, setRiskyActivitiesInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [familyMedicalHistoryInformation, setFamilyMedicalHistoryInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [insuranceInformation, setInsuranceInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [beneficiaryInformation, setBeneficiaryInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [medicalInformation, setMedicalInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [physicalInformation, setPhysicalInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [policyOwnerInformation, setPolicyOwnerInformation] = useState<Map<string, ConfirmationData>>(new Map());
    const [applicationSummary, setApplicationSummary] = useState<ApplicationSummary[]>([]);
    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [state, setState] = useState<string>('');
    const [country, setCountry] = useState<string>('');
    const [zipCode, setZipCode] = useState<string>('');
    const language: "es" | "en" | "pt" = 'pt';
    const currency: string = 'R$ ';
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // Same as MX: this provider wraps every BR route, but only the confirmation
    // screen reads the legacy summary. /application must not pay for it.
    const loadsLegacySummary = pathname.includes('/confirmation');

    const backToApplication = () => {
        navigate(`/application?quoteID=${quoteID}`);
    }

    const getPageIdByTemplateId = (templateId: string): number => {
        const page = applicationSummary.find((page) => page.template_page_id === templateId);
        return page ? page.id : 0;
    };

    useEffect(() => {
        if (applicationSummary?.length > 0 && JSON.stringify([...coverageInformation]) !== JSON.stringify([...defaultCoverageInformation])) {
            setIsLoading(false);
        }
    }, [coverageInformation, applicationSummary]);

    useEffect(() => {
        if (selected && lastQuote) {
            setCoverageInformation(updateCoverageInformation(selected, lastQuote, coverageInformation));
        }
    }, [selected, lastQuote]);

    useEffect(() => {
        if (quoteID && loadsLegacySummary) {
            fetchApplicationSummary(quoteID);
        }
    }, [quoteID, loadsLegacySummary]);

    // Función para forzar la recarga de datos
    const refreshData = async () => {
        if (quoteID) {
            setIsLoading(true);
            await fetchApplicationSummary(quoteID);
        }
    };

    useEffect(() => {
        const loadApplicationData = async () => {
            if (applicationSummary) {
                const pagePersonalInformationOfTheInsured = applicationSummary.find((page) => page.template_page_id === '14ef8ecc-2d2e-4ca4-beb6-1bbea819fa44');
                setPersonalInformationOfTheInsured(updateSummaryPageInfo(pagePersonalInformationOfTheInsured?.json, overWritePersonalInformationOfTheInsured, language));

                const pagePolicyOwnerInformation = applicationSummary.find((page) => page.template_page_id === "749424c7-544a-402b-8569-dd6518296bc1");
                setPolicyOwnerInformation(updateSummaryPageInfo(pagePolicyOwnerInformation?.json, overWritePolicyOwnerInformation, language));

                const pageOccupationInformation = applicationSummary.find((page) => page.template_page_id === "70397eeb-98b8-48c4-832c-f12a72452275");
                setOccupationInformation(updateSummaryPageInfo(pageOccupationInformation?.json, overWriteOccupationInformation, language, false, false, { "9b65fd68-c0db-402f-aa1b-281b083c1ece": "Ocupação" }, currency));

                const pageIncomeInformation = applicationSummary.find((page) => page.template_page_id === "51c76610-ac83-4fc8-a97e-7b7f040bf508");
                setIncomeInformation(updateSummaryPageInfo(pageIncomeInformation?.json, overWriteIncomeInformation, language, undefined, undefined, undefined, currency));

                const pagePoliticallyExposedPersonInformation = applicationSummary.find((page) => page.template_page_id === "05d990e3-2b99-4c34-9373-2535178626e7");
                setPoliticallyExposedPersonInformation(updateSummaryPageInfo(pagePoliticallyExposedPersonInformation?.json, overWritePoliticallyExposedPersonInformation, language, true, false));

                const pagePhysicalInformation = applicationSummary.find((page) => page.template_page_id === "4b248231-778e-4f68-94ba-1f2ec6354e7e");
                setPhysicalInformation(updateSummaryPageInfo(pagePhysicalInformation?.json, overWritePhysicalInformation, language, true, false, {
                    "c945a3f8-c08c-4bf9-a918-498baa790a29": "Substância",
                }));

                const riskyActivitiesPageIds = [
                    "5d12c0d8-69b2-44f0-a16e-9f058e73dc35",
                    "4c086505-7b6e-4a28-9cf2-59abff2b54ba",
                    "028155bc-9973-4d09-bd18-c171da7e3ad6",
                    "4428d3e3-447a-4ab5-a197-6151fce6747e",
                    "2c6b1147-4769-40df-bfdd-c0d05b12efa5",
                    "da324fc3-ddb4-4914-8026-86bfefe5ab25",
                    "b79cf1a8-50e3-47e3-8166-1597625b463a",
                    "ecdcdc53-5fdb-475d-bf55-89706a01bc60",
                    "c76c6346-ffb5-4bf3-b8c4-be14aaad2f0c",
                    "715d061e-a8d6-4bf2-b54a-9c82979504d7"
                ];

                setRiskyActivitiesInformation(
                    combinePagesInOrder(
                        riskyActivitiesPageIds,
                        applicationSummary,
                        (json, lang, pep, risky) => updateSummaryPageInfo(json, overWriteRiskyActivitiesInformation, lang, pep, risky, {
                            "dd333cb6-a6ce-4966-bed9-0e756f57c0e8": "Certificação",
                            "7d86ff28-4258-4a23-a58b-4e3bc6bca6f4": "Certificação de especialidade",
                            "a2fa9250-01ad-4407-9135-95752f36a441": "Tipo de mergulho",
                            "2c15ca10-3702-4383-bde7-9436990f88a4": "Localização",
                            "e2919699-2110-4b6c-a17f-a2be76735317": "Localização",
                        }),
                        language,
                        true,
                        false
                    )
                );

                const medicalPageIds = [
                    "2533618e-edf8-4ce8-85e2-bd58e3b8a67b",
                    "0bc42c4c-394d-4660-9475-29443d16409d",
                    "f3809d0b-84cc-4493-a119-413013ddc013",
                    "96d3ae71-ba71-4ba9-aa68-4087cb3d84c6",

                    "8b707c76-e317-4d76-99ce-b3048b14ed47",
                    "0b2017ae-40ed-44d9-bb0b-5621bb36bf42",

                    "42a030c4-ce03-460f-8aab-57e12dccbd66",
                    "b1789d07-637a-4eea-bb0c-eefdd09c3d45",
                    "53098018-eef4-4c24-8649-eac27ead4ab0",
                    "86aa66f6-5854-4944-aba4-2d2f1f980fd9",

                    "2e23985d-a7d3-4cdd-9885-d17edd4a3a0e",

                    "59b71d28-ffa1-4239-a8b1-cf14c32b5a33",
                    "96562692-5d4d-42c5-90dd-e9d7bb79726f",

                    "b55ffd2b-2198-45a1-99b5-8c4f69e8a780",

                    "ab13bfe8-7bdc-45a6-b2c2-d42cf814d886",

                    "fe2c8497-2677-41df-a74b-a88adbd7fc59",
                    "8ac7eb81-0b4b-477e-a380-a523d9a16646",
                    "ba8475e7-330b-471b-8acd-16f07cfdddae",
                    "8ed21ce7-9f60-428a-b693-7b44baa486fa",
                    "8e181874-0f0f-4e21-9923-0566e033b395",

                    "d50cdad1-d3fb-4144-b368-8666d668eb99",

                    "ebffc98c-a8fc-4d21-9cd1-d2c16f30b0f7",
                    "a16829b8-0b83-4628-8d89-746d8ca28306",
                    "5e80e2d6-2ecd-42fe-ae34-d6cd2c2adb47",
                    "a98db041-2c72-49fa-abb2-9a5cd5ee8c7a",

                    "1d12459f-35ea-43ff-bf64-332761193962",
                    "213a1a7a-6a08-4fd0-9b77-b4f05c32cfc4",
                    "01adccde-adb5-4e13-9e20-4616537cf8e3",

                    "e5d5e125-96f4-44ff-8def-533844afcc04",

                    "f9f6a8d3-3bcb-4a74-a4c7-018e846acf43",
                ];

                setMedicalInformation(
                    combinePagesInOrder(
                        medicalPageIds,
                        applicationSummary,
                        (json, lang, pep, risky) => updateSummaryPageInfo(json, overWriteMedicalInformation, lang, pep, risky),
                        language,
                        true,
                        false
                    )
                );


                const pageFamilyMedicalHistoryInformation = applicationSummary.find((page) => page.template_page_id === "1daea9f5-0078-4786-ab2a-e2cbff5a533d");
                setFamilyMedicalHistoryInformation(updateSummaryPageInfo(pageFamilyMedicalHistoryInformation?.json, overWriteFamilyMedicalHistoryInformation, language, true, false));

                const pageInsuranceInformation = applicationSummary.find((page) => page.template_page_id === "c4bed56d-d1dc-44bc-bda3-81f4d14fb17a");
                setInsuranceInformation(updateSummaryPageInfo(pageInsuranceInformation?.json, overWriteInsuranceInformation, language, true, false, {
                    "813e1cf6-e9e1-4bc0-81e9-90acd0fc55f5": "Seguro de vida"
                }, currency));

                const pageBeneficiaryInformation = applicationSummary.find((page) => page.template_page_id === "ee419a14-2bb1-45c3-8f7d-2ffdd4df173e");
                setBeneficiaryInformation(updateSummaryPageInfo(pageBeneficiaryInformation?.json, overWriteBeneficiaryInformation, language, true, false, {
                    "c1848b54-6351-449f-92af-9455b97e1eac": "Beneficiário",
                }));

                if (pagePersonalInformationOfTheInsured) {
                    setEmail(findValue(pagePersonalInformationOfTheInsured?.json, '1c5e3d28-481e-4d64-be0c-07529a1ef7e3', '1c5e3d28-481e-4d64-be0c-07529a1ef7e3', "a1c1f62e-326b-46f0-bb7d-33d436ca267d"));
                    setFirstName(findValue(pagePersonalInformationOfTheInsured?.json, '1c5e3d28-481e-4d64-be0c-07529a1ef7e3', '1c5e3d28-481e-4d64-be0c-07529a1ef7e3', "773e8c82-3c69-4745-8520-43ac32b1b030"));
                    setLastName(findValue(pagePersonalInformationOfTheInsured?.json, '1c5e3d28-481e-4d64-be0c-07529a1ef7e3', '1c5e3d28-481e-4d64-be0c-07529a1ef7e3', "84f4b5e2-a240-4405-839b-a35924df2fa8"));
                    setPhone(findValue(pagePersonalInformationOfTheInsured?.json, '1c5e3d28-481e-4d64-be0c-07529a1ef7e3', '1c5e3d28-481e-4d64-be0c-07529a1ef7e3', "103a15f4-0d02-496a-bf0f-994ee9e4b945"));
                    setAddress(findValue(pagePersonalInformationOfTheInsured?.json, '87b4d97c-d1c9-4619-86ad-12f60570f7db', '50af835f-451e-46c2-a1be-8f4da452f30e', "a75a9b18-e3e9-4631-b638-ea28bdf76190"));
                    setCity(findValue(pagePersonalInformationOfTheInsured?.json, '87b4d97c-d1c9-4619-86ad-12f60570f7db', '50af835f-451e-46c2-a1be-8f4da452f30e', "34379aab-6f0f-4c7d-8329-36eb870526c0"));
                    setState(findValue(pagePersonalInformationOfTheInsured?.json, '87b4d97c-d1c9-4619-86ad-12f60570f7db', '50af835f-451e-46c2-a1be-8f4da452f30e', "7c4e50cc-4488-4bf6-9388-1f1485fb473e"));
                    setCountry(findValue(pagePersonalInformationOfTheInsured?.json, '87b4d97c-d1c9-4619-86ad-12f60570f7db', '50af835f-451e-46c2-a1be-8f4da452f30e', "ce33e4e7-1939-4a45-84b4-a5788c0c6aff"));
                    setZipCode(findValue(pagePersonalInformationOfTheInsured?.json, '87b4d97c-d1c9-4619-86ad-12f60570f7db', '50af835f-451e-46c2-a1be-8f4da452f30e', "cd229724-cb09-4e70-9759-3fcd22e70723"));
                }
            }
        };

        if (applicationSummary) {
            loadApplicationData();
        }
    }, [applicationSummary]);

    const fetchApplicationSummary = async (quoteID: string) => {
        const summary = await getApplicationSummary(quoteID);
        setApplicationSummary(summary);
    };

    const findValueByGroupAndQuestion = (groupAndQuestionId: string): string | undefined => {
        const [groupID, questionID] = groupAndQuestionId.split('|');

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (!section.groups) continue;

                for (const group of section.groups) {
                    if (group.group_id === groupID) {
                        if (group.hidden) {
                            return undefined;
                        }
                        const field = group.fields?.find(field => field.question_id === questionID);
                        if (field) {
                            if (field.hidden) {
                                return undefined;
                            }
                            return field.value || field.value_default;
                        }
                    }
                }
            }
        }

        return undefined;
    };

    const getFieldsBySectionId = (sectionId: string, excludeGroupId?: string): any[] => {
        const results: any[] = [];
        const questionIdToKeyMap: { [key: string]: string } = {
            "9660b5e2-2325-4101-b3fe-e316e3ff20a1": "first_name",
            "791a596a-4cfa-479a-90bb-1d0f79c3d621": "last_name",
            "32c72e92-0aa6-4582-a1fa-737c243ed69f": "document_number",
            "90806065-0d4c-4818-a31e-05d691195368": "birth_date",
            "1170127e-b2ff-4665-944d-622f44da403e": "relationship",
            "e00cd1a2-7eaf-4e36-ab0a-97dfe49ec7bc": "percent"
        };

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (section.section_id === sectionId) {
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;
                        if (excludeGroupId && group.group_id === excludeGroupId) continue;

                        if (group.fields) {
                            const groupData: { [key: string]: any } = {
                                type: "1"
                            };
                            let hasUserValue = false;
                            for (const field of group.fields) {
                                if (field.hidden) continue;

                                const key = questionIdToKeyMap[field.question_id];
                                if (key) {
                                    const value = field.value || field.value_default || '';
                                    groupData[key] = value;
                                    if (value !== '') hasUserValue = true;
                                }
                            }
                            // Solo agregar el beneficiario si tiene al menos un dato real del usuario.
                            // Evita enviar beneficiarios vacíos (solo type/class/document_type/cep) al backend.
                            if (hasUserValue) {
                                groupData['beneficiary_class'] = '1';
                                groupData['document_type'] = '7';
                                groupData['cep'] = zipCode;
                                results.push(groupData);
                            }
                        }
                    }
                }
            }
        }

        return results;
    };

    const findGroupWithBothConditions = (sectionId: string, questionId1: string, value1: string, questionId2: string, value2: string): boolean => {
        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (section.section_id === sectionId) {
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;
                        if (!group.fields) continue;

                        let field1Found = false;
                        let field2Found = false;

                        for (const field of group.fields) {
                            if (field.hidden) continue;

                            const fieldValue = field.value || field.value_default || '';

                            if (field.question_id === questionId1 && fieldValue === value1) {
                                field1Found = true;
                            }

                            if (field.question_id === questionId2 && fieldValue === value2) {
                                field2Found = true;
                            }

                            // Verificar inmediatamente si ambas condiciones se han cumplido
                            if (field1Found && field2Found) {
                                return true;
                            }
                        }
                    }
                }
            }
        }

        return false;
    };

    const getHealthInsuranceBySectionId = (sectionId: string, excludeGroupId?: string): Array<{ company: string; coverage_amount: number | null }> => {
        const results: Array<{ company: string; coverage_amount: number | null }> = [];

        // QuestionIds para health insurance en Brasil
        const companyQuestionId = "3b5d755a-0de8-4cdc-b58f-5fc789f527a0";
        const coverageAmountQuestionId = "3cb46b20-c80d-48b4-8e7d-5c2f374c4b59";

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (section.section_id === sectionId) {
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;
                        if (excludeGroupId && group.group_id === excludeGroupId) continue;

                        let company: string | undefined;
                        let coverageAmount: string | undefined;

                        if (group.fields) {
                            for (const field of group.fields) {
                                if (field.hidden) continue;

                                if (field.question_id === companyQuestionId) {
                                    company = field.value || field.value_default || undefined;
                                } else if (field.question_id === coverageAmountQuestionId) {
                                    coverageAmount = field.value || field.value_default || undefined;
                                }
                            }
                        }

                        if (company) {
                            // Limpiar el coverageAmount eliminando comas antes de convertir a número
                            const cleanAmount = coverageAmount ? coverageAmount.replace(/,/g, '') : null;
                            const amount = cleanAmount ? Number(cleanAmount) : null;
                            
                            results.push({
                                company: company,
                                coverage_amount: amount
                            });
                        }
                    }
                }
            }
        }

        return results;
    };

    const getOccupationBySectionId = (sectionId: string, excludeGroupId?: string): Array<{ name: string; description: string; main?: boolean; other_name?: string }> => {
        const results: Array<{ name: string; description: string; main?: boolean; other_name?: string }> = [];

        // QuestionIds para occupation en Brasil
        const nameQuestionId = "dd478d98-b7f5-4a26-932a-c29de6aebf0f";
        const descriptionQuestionId = "d48bef3d-30b6-4f07-897e-2ecc7013a0ad"; // No existe en BR
        const otherNameQuestionId = "a931b359-1a01-421a-85ee-96ecf6b40f01";
        const mainGroupId = "2f6949a9-24a5-4ec1-bc58-f79b16a9a95b";

        for (const page of applicationSummary) {
            if (!page.json?.sections) continue;

            for (const section of page.json.sections) {
                if (section.section_id === sectionId) {
                    if (!section.groups) continue;

                    for (const group of section.groups) {
                        if (group.hidden) continue;
                        if (excludeGroupId && group.group_id === excludeGroupId) continue;

                        let name: string | undefined;
                        let description: string | undefined;
                        let otherName: string | undefined;

                        if (group.fields) {
                            for (const field of group.fields) {
                                if (field.question_id === otherNameQuestionId) {
                                    otherName = field.value || field.value_default || undefined;
                                    continue;
                                }
                                if (field.hidden) continue;

                                if (field.question_id === nameQuestionId) {
                                    name = field.value || field.value_default || undefined;
                                } else if (field.question_id === descriptionQuestionId) {
                                    description = field.value || field.value_default || undefined;
                                }
                            }
                        }

                        if (name) {
                            const item: { name: string; description: string; main?: boolean; other_name?: string } = {
                                name: name,
                                description: description || ""
                            };
                            if (group.group_id === mainGroupId) {
                                item.main = true;
                            }
                            if (name === '129' && otherName) {
                                item.other_name = otherName;
                            }
                            results.push(item);
                        }
                    }
                }
            }
        }

        return results;
    };

    return (
        <BrConfirmationContext.Provider value={{
            isLoading,
            setIsLoading,
            coverageInformation,
            setCoverageInformation,
            personalInformationOfTheInsured,
            setPersonalInformationOfTheInsured,
            occupationInformation,
            setOccupationInformation,
            incomeInformation,
            setIncomeInformation,
            politicallyExposedPersonInformation,
            setPoliticallyExposedPersonInformation,
            riskyActivitiesInformation,
            setRiskyActivitiesInformation,
            familyMedicalHistoryInformation,
            setFamilyMedicalHistoryInformation,
            insuranceInformation,
            setInsuranceInformation,
            beneficiaryInformation,
            setBeneficiaryInformation,
            medicalInformation,
            setMedicalInformation,
            physicalInformation,
            setPhysicalInformation,
            policyOwnerInformation,
            setPolicyOwnerInformation,
            email,
            setEmail,
            firstName,
            setFirstName,
            lastName,
            setLastName,
            phone,
            setPhone,
            address,
            setAddress,
            city,
            setCity,
            state,
            setState,
            country,
            setCountry,
            zipCode,
            setZipCode,
            backToApplication,
            getPageIdByTemplateId,
            findValueByGroupAndQuestion,
            getFieldsBySectionId,
            findGroupWithBothConditions,
            getHealthInsuranceBySectionId,
            getOccupationBySectionId,
            refreshData
        }}>
            {children}
        </BrConfirmationContext.Provider>
    );
};

export const useBrConfirmation = () => {
    const context = useContext(BrConfirmationContext);
    if (context === undefined) {
        throw new Error('useConfirmation must be used within a ConfirmationProvider');
    }
    return context;
};

